// src/pages/scheduling/Scheduling.jsx
import { useRef, useState, useEffect } from "react";
import { Button, Card, DataPageCard, PaginatedTable, Page, Tabs, showApiError } from "@common";
import { usePractitioners, useUpdatePractitioner, useUploadPractitioners } from "@api/hooks";
import { EditPractitionerModal } from "@common";
import { normalizeTypeForUi } from "@utils/utils";
import toast from "react-hot-toast";

export default function Scheduling() {
  const [tab, setTab] = useState("practitioners");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPractitioner, setEditingPractitioner] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    clinic: "",
    practitioner_type: "",
    status: "",
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQ(q);
      setPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [q]);

  // Fetch practitioners with filters and auto-refresh
  const {
    data: practitionersData,
    refetch,
    isLoading,
    error,
  } = usePractitioners(
    {
      search: debouncedQ,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    },
    {
      enablePolling: true,
    },
  );

  // Update practitioner mutation
  const updatePractitionerMutation = useUpdatePractitioner();

  // Upload practitioners mutation
  const uploadPractitionersMutation = useUploadPractitioners();

  // Refetch when debounced search query or pagination changes
  useEffect(() => {
    refetch();
  }, [debouncedQ, page, pageSize, refetch]);

  const data = practitionersData?.results || [];
  const total = practitionersData?.count || 0;

  // Handle edit click
  const handleEdit = (practitioner) => {
    setEditingPractitioner(practitioner);
    setFormData({
      name: practitioner.name || "",
      clinic: practitioner.clinic || "",
      practitioner_type: normalizeTypeForUi(practitioner?.practitioner_type).toLowerCase() || "",
      status: (practitioner.status || "active").toLowerCase(),
    });
    setIsEditModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle save
  const handleSave = async () => {
    if (!editingPractitioner) return;

    try {
      await updatePractitionerMutation.mutateAsync({
        id: editingPractitioner.id,
        data: formData,
      });
      setIsEditModalOpen(false);
      setEditingPractitioner(null);
      refetch(); // Refresh the list
    } catch (error) {
      console.error("Failed to update practitioner:", error);
      alert("Failed to update practitioner. Please try again.");
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditModalOpen(false);
    setEditingPractitioner(null);
  };

  const columns = [
    { key: "clinic", header: "Clinic", nowrap: true },
    { key: "name", header: "Name" },
    {
      key: "practitioner_type",
      header: "Type",
      nowrap: true,
      render: (row) => row.practitioner_type?.toUpperCase() || "N/A",
    },
    {
      key: "status",
      header: "Status",
      nowrap: true,
      render: (row) => (
        <span
          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
            row.status?.toLowerCase() === "active"
              ? "bg-palette-green-muted text-palette-green-text"
              : "bg-palette-gray-subtle text-palette-gray-text"
          }`}
        >
          {row.status || "Active"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      nowrap: true,
      render: (row) => (
        <Button
          size="sm"
          variant="primary"
          className="rounded-full px-4 py-1.5 bg-palette-indigo-badge text-white hover:bg-palette-indigo-heading"
          onClick={() => handleEdit(row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  // Upload handlers
  const fileInputRef = useRef(null);
  const templateHref = "/templates/practitioner_upload_template.csv"; // place file in /public/templates

  function triggerUpload() {
    fileInputRef.current?.click();
  }

  async function onFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const ok =
      file.name.endsWith(".xlsx") || file.name.endsWith(".xls") || file.name.endsWith(".csv");
    if (!ok) {
      toast.error("Please select a .xlsx, .xls, or .csv file.");
      e.target.value = "";
      return;
    }

    try {
      toast.loading("Uploading practitioners...", { id: "upload" });
      const result = await uploadPractitionersMutation.mutateAsync(file);

      toast.dismiss("upload");

      if (result.success) {
        toast.success(
          result.message || `Successfully uploaded ${result.summary.created} practitioner(s)`,
        );

        if (result.summary.skipped > 0) {
          toast.success(`${result.summary.skipped} practitioner(s) already exist and were skipped`);
        }
        if (result.summary.errors > 0) {
          toast.error(`${result.summary.errors} practitioner(s) had errors`);
        }
      } else {
        toast.error(result.message || "Upload completed with errors");
      }

      refetch();
    } catch (error) {
      toast.dismiss("upload");
      showApiError(error);
      console.error("Upload error:", error);
    }

    e.target.value = "";
  }

  return (
    <Page>
      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: "practitioners", label: "Practitioners" },
          { key: "upload", label: "Upload Practitioners" },
        ]}
      />

      {/* Practitioners list */}
      {tab === "practitioners" && (
        <Card title="Practitioners">
          <p className="text-palette-gray-secondary -mt-1 mb-3">
            View and manage clinic practitioners below.
          </p>

          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
            }}
            placeholder="Search for Practitioners by Clinic or Name..."
            className="w-full rounded-xl border border-palette-gray-divider px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-palette-indigo-border"
          />

          <div className="mt-4" />

          {/* ✅ LOADING HANDLED HERE (prevents “No data available” flash) */}
          <DataPageCard
            className="shadow-none"
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && !error && data.length === 0}
            loadingMessage="Loading practitioners..."
            emptyMessage="No practitioners found"
            showLoadingSpinner={true}
          >
            <PaginatedTable
              mode="server"
              columns={columns}
              data={data}
              rowKey={(r) => r.id}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPage(1);
                setPageSize(n);
              }}
            />
          </DataPageCard>
        </Card>
      )}

      {/* Upload Practitioners */}
      {tab === "upload" && (
        <Card className="rounded-2xl shadow-md bg-palette-indigo-soft border border-palette-indigo-muted">
          <h3 className="text-[18px] font-bold mb-2 text-palette-indigo-primary">
            Add New Practitioners
          </h3>

          <p className="text-palette-indigo-primary/80">
            To add new practitioners, download our Excel template, fill it out following the same
            columns, and upload it.
          </p>

          <div className="mt-5 mb-2 flex justify-center">
            <Button
              as="a"
              href={templateHref}
              download
              variant="primary"
              className="rounded-full px-5 py-2.5 shadow-sm bg-palette-indigo-badge text-white hover:bg-palette-indigo-heading inline-flex items-center gap-2"
            >
              <span className="-mt-[1px]">⬇️</span>
              <span>Download Template</span>
            </Button>
          </div>

          <div className="my-5 border-t border-palette-indigo-border/60" />

          <p className="text-center text-palette-indigo-primary/80 mb-4">
            After filling out the template, click the button below to upload the file. Please ensure
            to follow the template format.
          </p>

          <div className="flex justify-center">
            <Button
              variant="primary"
              className="rounded-full px-5 py-2.5 bg-palette-indigo-badge text-white hover:bg-palette-indigo-heading inline-flex items-center gap-2"
              onClick={triggerUpload}
            >
              <span className="-mt-[1px]">📎</span>
              <span>Upload Practitioners</span>
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={onFileSelected}
              className="hidden"
            />
          </div>
        </Card>
      )}

      {/* Edit Practitioner Modal */}
      <EditPractitionerModal
        isOpen={isEditModalOpen}
        onClose={handleCancel}
        onSave={handleSave}
        formData={formData}
        onInputChange={handleInputChange}
        isPending={updatePractitionerMutation.isPending}
      />
    </Page>
  );
}
